/**
 * Utility functions for string comparison using localeCompare
 */

/**
 * Compares two strings using localeCompare for proper string comparison
 * Returns 0 if strings are equal, negative if str1 < str2, positive if str1 > str2
 */
export const compareStrings = (str1, str2, locale = 'pl-PL') => {
  if (!str1 && !str2) return 0;
  if (!str1) return -1;
  if (!str2) return 1;
  
  return str1.localeCompare(str2, locale, {
    sensitivity: 'base', // Ignore case and diacritics
    numeric: true // Sort numbers naturally
  });
};

/**
 * Checks if two strings are equal using localeCompare
 */
export const areStringsEqual = (str1, str2, locale = 'pl-PL') => {
  return compareStrings(str1, str2, locale) === 0;
};

/**
 * Finds the best matching string from an array using localeCompare
 */
export const findBestMatch = (searchString, candidates, locale = 'pl-PL') => {
  if (!searchString || !candidates || candidates.length === 0) return null;
  
  // Exact match first
  const exactMatch = candidates.find(candidate => 
    areStringsEqual(candidate, searchString, locale)
  );
  
  if (exactMatch) return exactMatch;
  
  // Partial match (contains) - check if one string contains the other
  const partialMatch = candidates.find(candidate => {
    if (!candidate) return false;
    
    // Convert both to lowercase for contains check
    const candidateLower = candidate.toLowerCase();
    const searchLower = searchString.toLowerCase();
    
    return candidateLower.includes(searchLower) || searchLower.includes(candidateLower);
  });
  
  return partialMatch || null;
};

/**
 * Sorts an array of strings using localeCompare
 */
export const sortStrings = (strings, locale = 'pl-PL') => {
  return [...strings].sort((a, b) => compareStrings(a, b, locale));
};

/**
 * Checks if a string contains another string using localeCompare
 * Useful for search functionality
 */
export const containsString = (text, searchTerm, locale = 'pl-PL') => {
  if (!text || !searchTerm) return false;
  
  const textLower = text.toLowerCase();
  const searchLower = searchTerm.toLowerCase();
  
  return textLower.includes(searchLower);
};

/**
 * Filters an array of strings based on search term using localeCompare
 */
export const filterStrings = (strings, searchTerm, locale = 'pl-PL') => {
  if (!searchTerm) return strings;
  
  return strings.filter(str => containsString(str, searchTerm, locale));
};

/**
 * Legacy functions for backward compatibility
 * @deprecated Use compareStrings and areStringsEqual instead
 */
export const normalizeString = (str) => {
  if (!str) return '';
  return str.toLowerCase().trim().replace(/\s+/g, ' ');
};

export const normalizeStringSimple = (str) => {
  if (!str) return '';
  return str.toLowerCase().trim().replace(/\s+/g, ' ');
};

// Debug function for backward compatibility
export const debugNormalization = (str) => {
  console.log('🔍 Debug string comparison:');
  console.log(`  Original: "${str}"`);
  console.log(`  Lowercase: "${str.toLowerCase()}"`);
  console.log(`  Trimmed: "${str.toLowerCase().trim()}"`);
  console.log(`  Normalized: "${normalizeString(str)}"`);
}; 